import { useEffect, useState } from 'react'

export type LoadingState = 'idle' | 'pending' | 'success' | 'error'

export interface SuccessAnimationConfig {
  /** Duration to show success animation before reverting (ms) */
  displayDuration?: number
  /** Animation speed multiplier */
  speed?: number
  /** Callback when animation completes and returns to idle */
  onAnimationComplete?: () => void
}

export interface DropdownAnimationState {
  currentState: LoadingState
  showIdle: boolean
  showPending: boolean
  showSuccess: boolean
  showError: boolean
}

export const DEFAULT_SUCCESS_CONFIG: SuccessAnimationConfig = {
  displayDuration: 3000,
  speed: 1,
}

export const useLoadingStateAnimation = (
  loadingState: LoadingState = 'idle',
  config: SuccessAnimationConfig = DEFAULT_SUCCESS_CONFIG,
): DropdownAnimationState => {
  const [currentState, setCurrentState] = useState<LoadingState>('idle')
  const [successTimeout, setSuccessTimeout] = useState<NodeJS.Timeout>()
  const [isSuccessCompleted, setIsSuccessCompleted] = useState(false)

  // Update current state based on prop changes
  useEffect(() => {
    // If we've completed a success animation and external state is still success, show idle
    if (loadingState === 'success' && isSuccessCompleted) {
      return
    }

    // Reset success completed flag when not in success state
    if (loadingState !== 'success' && isSuccessCompleted) {
      setIsSuccessCompleted(false)
    }

    if (loadingState !== currentState) {
      // Clear any existing timeout when state changes
      if (successTimeout) {
        clearTimeout(successTimeout)
        setSuccessTimeout(undefined)
      }

      setCurrentState(loadingState)

      // Auto-transition from success back to idle after display duration
      if (loadingState === 'success' && !isSuccessCompleted) {
        const timeout = setTimeout(() => {
          setCurrentState('idle')
          setIsSuccessCompleted(true)
          config.onAnimationComplete?.()
        }, config.displayDuration || DEFAULT_SUCCESS_CONFIG.displayDuration!)

        setSuccessTimeout(timeout)
      }
    }
  }, [loadingState, currentState, config, successTimeout, isSuccessCompleted])

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (successTimeout) {
        clearTimeout(successTimeout)
      }
    }
  }, [successTimeout])

  const displayState =
    isSuccessCompleted && loadingState === 'success' ? 'idle' : currentState

  return {
    currentState: displayState,
    showIdle: displayState === 'idle',
    showPending: displayState === 'pending',
    showSuccess: displayState === 'success',
    showError: displayState === 'error',
  }
}
